import React from 'react';
import { jsx } from '@emotion/core';
import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import Modal from '../../../components/modal/Modal';
import Input from '../../../components/input/Input';
import { IModal } from '../../../utils/types';
import theme from '../../../theme/theme';
import UpdateHandlers from '../../../utils/handlers';
import {
  useUpdate_Income_SourceMutation,
  useAdd_Income_SourceMutation,
} from '../../../generated/graphql';
import Flex from '../../../components/layout/Flex';
import ErrorMessage from '../../../components/ErrorMessage';
import { useAuthState } from '../../../context/auth';
/**@jsx jsx*/

interface ValueProps {
  name: string;
}

type IncomeSourceModal = IModal & {
  incomeSource?: { id: number; name: string };
};

const IncomeSourceSchema = Yup.object().shape({
  name: Yup.string().required('required'),
});

const IncomeSourceModal: React.FC<IncomeSourceModal> = ({
  onClose,
  incomeSource,
}) => {
  const { userId } = useAuthState();
  const [initialValues, setInitialValues] = React.useState<ValueProps>({
    name: '',
  });

  const [
    addIncomeSource,
    {
      data: addIncomeSourceResult,
      loading: addIncomeSourceLoading,
      error: addIncomeSourceError,
    },
  ] = useAdd_Income_SourceMutation();

  const [
    updateIncomeSource,
    {
      data: updateIncomeSourceResult,
      loading: updateIncomeSourceLoading,
      error: updateIncomeSourceError,
    },
  ] = useUpdate_Income_SourceMutation();

  React.useEffect(() => {
    if (incomeSource) {
      setInitialValues({
        name: incomeSource.name,
      });
    }
  }, [incomeSource]);

  const addNewIncomeSource = (values: ValueProps) => {
    addIncomeSource({
      variables: {
        changes: [
          {
            name: values.name,
            created_by: userId,
          },
        ],
      },
      update: UpdateHandlers.addIncomeSource,
    }).catch(() => {});
  };

  const upateExistingIncomeSource = (values: ValueProps) => {
    updateIncomeSource({
      variables: {
        id: incomeSource?.id,
        changes: {
          name: values.name,
          modified_by: userId,
        },
      },
    }).catch(() => {});
  };

  const handleSubmit = (
    values: ValueProps,
    { setSubmitting }: FormikHelpers<ValueProps>
  ) => {
    if (incomeSource) {
      upateExistingIncomeSource(values);
    } else {
      addNewIncomeSource(values);
    }
    setSubmitting(false);
  };

  const loading = !!(addIncomeSourceLoading || updateIncomeSourceLoading);
  const error = !!(addIncomeSourceError || updateIncomeSourceError);
  const success = !!(
    addIncomeSourceResult?.insert_income_source?.returning?.length ||
    updateIncomeSourceResult?.update_income_source?.returning?.length
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={IncomeSourceSchema}
      enableReinitialize>
      {(formikProps) => (
        <Form>
          <Modal
            onClose={onClose}
            cancel={{ label: 'Cancel', function: onClose }}
            confirm={{
              type: 'submit',
              label: `${incomeSource ? 'Update' : 'Add'}`,
              loading: formikProps.isSubmitting || loading,
              icon: {
                name: 'plus',
                color: theme.colors.white,
                position: 'after',
              },
            }}
            icon={{ name: 'building-columns' }}
            title={`${incomeSource ? 'Update' : 'Add'} Income Source`}>
            <React.Fragment>
              {error && (
                <Flex
                  jc="center"
                  css={{ color: theme.colors.red[500] }}
                  className="add-failed">
                  Operation failed...please try again
                </Flex>
              )}
              {success && (
                <Flex
                  jc="center"
                  css={{ color: theme.colors.green[500] }}
                  className="add-success">
                  {`Income Source ${
                    incomeSource ? 'updated' : 'added'
                  } successfully...`}
                </Flex>
              )}
              <div>
                <Input
                  type="text"
                  name="name"
                  placeholder="Enter name"
                  label="Income Source"
                  onChange={formikProps.handleChange}
                  onBlur={formikProps.handleBlur}
                  value={formikProps.values.name}
                />
                <ErrorMessage name="name" />
              </div>
            </React.Fragment>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};

export default IncomeSourceModal;
